import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { FileText, Building2, TrendingUp, TrendingDown, Wallet, Calendar, AlertCircle } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';

const BankBook = () => {
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [bankInfo, setBankInfo] = useState(null);
  const [summary, setSummary] = useState({ total_deposits: 0, total_withdrawals: 0, opening_balance: 0, current_balance: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { fetchBanks(); }, []);

  const fetchBanks = async () => { 
    try { 
      const response = await apiClient.get('/api/accounts/banks');
      setBanks(Array.isArray(response.data) ? response.data : []); 
    } catch (err) { 
      console.error('Error:', err); 
      setBanks([]);
    } 
  };

  const fetchReport = async () => {
    if (!selectedBank) return;
    
    setLoading(true);
    setError(null);
    try {
      let url = `/api/accounts/bank-transactions/${selectedBank}`;
      const params = [];
      if (startDate) params.push(`from_date=${startDate}`);
      if (endDate) params.push(`to_date=${endDate}`);
      if (params.length > 0) url += '?' + params.join('&');

      const response = await apiClient.get(url);
      const data = response.data;
      
      setTransactions(data.transactions || []);
      setSummary(data.summary || { total_deposits: 0, total_withdrawals: 0, opening_balance: 0, current_balance: 0 });
      setBankInfo(data.bank || null);
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load bank transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    if (selectedBank) fetchReport(); 
  }, [selectedBank]);

  const getRunningBalance = (index) => {
    let balance = summary.opening_balance || 0;
    const reversedTransactions = [...transactions].reverse();
    for (let i = 0; i <= (transactions.length - 1 - index); i++) {
      const txn = reversedTransactions[i];
      if (txn.type === 'Deposit') {
        balance += txn.amount;
      } else {
        balance -= txn.amount;
      }
    }
    return balance;
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            Bank Book
          </CardTitle>
          <div className="flex gap-2 items-center flex-wrap">
            <Select value={selectedBank} onValueChange={setSelectedBank}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Select Bank Account" />
              </SelectTrigger>
              <SelectContent>
                {banks.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.bank_name} - {b.account_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-36" />
              <span className="text-gray-500">to</span>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-36" />
            </div>
            <Button onClick={fetchReport} disabled={loading || !selectedBank}>
              <FileText className="w-4 h-4 mr-2" />{loading ? 'Loading...' : 'Generate'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={fetchReport} className="ml-auto">Retry</Button>
            </div>
          )}

          {selectedBank && bankInfo ? (
            <>
              <div className="bg-gray-50 p-3 rounded-lg mb-4 flex items-center justify-between">
                <div>
                  <span className="font-semibold">{bankInfo.bank_name}</span>
                  <span className="text-gray-500 ml-2">A/C: {bankInfo.account_number}</span>
                </div>
                <div className="text-sm text-gray-600">
                  Opening Balance: <span className="font-semibold">₹{summary.opening_balance?.toLocaleString('en-IN')}</span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    <p className="text-sm text-green-600">Total Deposits</p>
                  </div>
                  <p className="text-2xl font-bold text-green-700">₹{summary.total_deposits?.toLocaleString('en-IN')}</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-red-600" />
                    <p className="text-sm text-red-600">Total Withdrawals</p>
                  </div>
                  <p className="text-2xl font-bold text-red-700">₹{summary.total_withdrawals?.toLocaleString('en-IN')}</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-blue-600" />
                    <p className="text-sm text-blue-600">Current Balance</p>
                  </div>
                  <p className={`text-2xl font-bold ${summary.current_balance >= 0 ? 'text-blue-700' : 'text-orange-600'}`}>
                    ₹{summary.current_balance?.toLocaleString('en-IN')}
                  </p>
                </div>
              </div>

              <div className="text-sm text-gray-500 mb-2">Showing {transactions.length} bank transactions</div>

              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3">Date</th>
                    <th className="text-left p-3">Type</th>
                    <th className="text-left p-3">Category</th>
                    <th className="text-left p-3">Description</th>
                    <th className="text-left p-3">Mode</th>
                    <th className="text-right p-3">Deposits (+)</th>
                    <th className="text-right p-3">Withdrawals (-)</th>
                    <th className="text-right p-3">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.length === 0 ? (
                    <tr><td colSpan="8" className="text-center p-8 text-gray-500">No bank transactions found for the selected period</td></tr>
                  ) : (
                    transactions.map((txn, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="p-3">{txn.date}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${txn.type === 'Deposit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {txn.type}
                          </span>
                        </td>
                        <td className="p-3">{txn.category}</td>
                        <td className="p-3 max-w-xs truncate">{txn.description || '-'}</td>
                        <td className="p-3 text-sm text-gray-600">{txn.payment_mode || '-'}</td>
                        <td className="p-3 text-right text-green-600 font-medium">{txn.type === 'Deposit' ? `+₹${txn.amount?.toLocaleString('en-IN')}` : '-'}</td>
                        <td className="p-3 text-right text-red-600 font-medium">{txn.type === 'Withdrawal' ? `-₹${txn.amount?.toLocaleString('en-IN')}` : '-'}</td>
                        <td className="p-3 text-right font-semibold">₹{getRunningBalance(idx)?.toLocaleString('en-IN')}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </>
          ) : (
            <p className="text-center text-gray-500 py-8">Please select a bank account to view transactions</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BankBook;
